/**
 * Book-content repository — the static_* stores plus the derived/cache rows
 * that live and die with a book (including the atomic reprocess replacement,
 * {@link BookContentRepo.replaceDerivedContent}).
 *
 * Worker-safe: the TTS engine worker imports this module (sections, TTS
 * preparation, table images). It must never import the yjs-backed Zustand
 * stores, sync services, React, or the EPUB ingestion pipeline — inventory
 * merging lives in app/repositories/BookRepository; book import lives in
 * domains/library/import.
 *
 * Write discipline (see write-gate.ts): every writer goes through `write()` —
 * one readwrite transaction opened inside the cross-context gate, populated
 * SYNCHRONOUSLY (intra-transaction awaits are WebKit hang trigger #2).
 * Read-modify-write sites read in a plain readonly transaction OUTSIDE the
 * gate first (last-write-wins between read and write).
 */
import { getConnection } from '../connection';
import { write } from '../write-gate';
import { handleDbError } from '../errors';
import type {
  StaticManifestRow,
  StaticStructureRow,
} from '../rows/static';
import type {
  CacheRenderMetricsRow,
  CacheTtsPreparationRow,
  TableImageRow,
} from '../rows/cache';
import { APP_METADATA_KEYS } from '../rows/app';
import type { NavigationItem, SectionMetadata } from '~types/book';
import type { BookLocations, TableImage } from '~types/cache';
import { DatabaseError } from '~types/errors';
import { createLogger } from '@lib/logger';

const logger = createLogger('BookContentRepo');

/**
 * The raw IndexedDB rows describing one book's static data. Consumed by
 * app/repositories/BookRepository, which merges in the yjs-backed user
 * inventory to produce a BookMetadata.
 */
export interface ManifestBundle {
  manifest: StaticManifestRow;
  structure: StaticStructureRow | undefined;
  /** Whether the binary EPUB resource is present locally (false ⇒ offloaded). */
  hasResource: boolean;
}

/** The ingestion payload's persisted slice (everything ingest() writes). */
export interface BookIngestData {
  bookId: string;
  manifest: StaticManifestRow & { coverBlob?: Blob | ArrayBuffer };
  resource: { bookId: string; epubBlob: Blob | ArrayBuffer };
  structure: StaticStructureRow;
  ttsContentBatches: CacheTtsPreparationRow[];
  tableBatches: (Omit<TableImageRow, 'imageBlob'> & { imageBlob: Blob | ArrayBuffer })[];
}

/**
 * One table snapshot's location — everything about a captured table EXCEPT
 * its pixels (see {@link BookContentRepo.listTableLocations}).
 */
export interface TableLocation {
  /** Row key: `${bookId}-${cfi}`. */
  id: string;
  /** CFI of the table element. */
  cfi: string;
  /** Section (href) the table lives in. */
  sectionId: string;
}

/** Derived rows regenerated by a reprocess (replaceDerivedContent). */
interface DerivedContentReplacement {
  /** Updated manifest row (already mutated by the caller); omitted when the book has no manifest. */
  manifest?: StaticManifestRow;
  structure: StaticStructureRow;
  ttsPrep: CacheTtsPreparationRow[];
  tableImages: (Omit<TableImageRow, 'imageBlob'> & { imageBlob: Blob | ArrayBuffer })[];
}

const toArrayBuffer = async (value: Blob | ArrayBuffer): Promise<ArrayBuffer> =>
  value instanceof Blob ? value.arrayBuffer() : Promise.resolve(value);

class BookContentRepo {
  // ── Reads ──────────────────────────────────────────────────────────────

  /**
   * Retrieves the raw static rows for multiple books in a single transaction.
   * Optimized for bulk hydration by querying specifically requested IDs within one transaction.
   * Preserves the exact index mapping of the input array.
   */
  async getManifestBundleBulk(ids: string[]): Promise<(ManifestBundle | undefined)[]> {
    try {
      if (ids.length === 0) return [];
      const db = await getConnection();
      const tx = db.transaction(['static_manifests', 'static_resources', 'static_structure'], 'readonly');

      const manifestStore = tx.objectStore('static_manifests');
      const resourceStore = tx.objectStore('static_resources');
      const structureStore = tx.objectStore('static_structure');

      // BOLT OPTIMIZATION: Avoid getAll() on large arrays across IDB bridge to prevent serialization OOMs
      // and use count() instead of getKey() to avoid fetching the key value itself.
      // FURTHER OPTIMIZATION: Chunk parallel requests to avoid flooding the transaction/microtask queue.
      const manifests: (StaticManifestRow | undefined)[] = [];
      const resourceCounts: number[] = [];
      const structures: (StaticStructureRow | undefined)[] = [];
      const CHUNK_SIZE = 100;

      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);
        const manifestsPromise = Promise.all(chunk.map(id => manifestStore.get(id)));
        const resourceCountPromise = Promise.all(chunk.map(id => resourceStore.count(id)));
        const structuresPromise = Promise.all(chunk.map(id => structureStore.get(id)));

        const [m, r, s] = await Promise.all([manifestsPromise, resourceCountPromise, structuresPromise]);
        manifests.push(...m);
        resourceCounts.push(...r);
        structures.push(...s);
      }

      await tx.done;

      const manifestsMap = new Map<string, ManifestBundle>();
      manifests.forEach((m, i) => {
        if (m) {
          manifestsMap.set(m.bookId, { manifest: m, structure: structures[i], hasResource: resourceCounts[i] > 0 });
        }
      });

      // Map results back preserving index and handling missing records
      return ids.map((id) => manifestsMap.get(id));
    } catch (error) {
      handleDbError(error);
    }
    return ids.map(() => undefined);
  }

  /**
   * Retrieves the raw static rows for a specific book.
   * Inventory merging (yjs) happens in BookRepository.
   */
  async getManifestBundle(id: string): Promise<ManifestBundle | undefined> {
    try {
      const db = await getConnection();
      const tx = db.transaction(['static_manifests', 'static_resources', 'static_structure'], 'readonly');

      const manifest = await tx.objectStore('static_manifests').get(id);
      const resourceKey = await tx.objectStore('static_resources').getKey(id);
      const structure = await tx.objectStore('static_structure').get(id);

      await tx.done;

      if (!manifest) {
        return undefined;
      }

      return { manifest, structure, hasResource: !!resourceKey };
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * ONE book's cover thumbnail, normalized to a Blob (rows persist it as an
   * ArrayBuffer for WebKit IDB compatibility). Returns undefined when the book
   * has no cover, or no manifest at all.
   *
   * perf (P-mem): this is the fallback lane's on-demand read. The merged
   * metadata (app/repositories/BookRepository) no longer carries cover bytes
   * while the page is service-worker controlled, so nothing pins the whole
   * library's thumbnails; when the SW route is unavailable, `useCoverUrl`
   * calls this for the covers actually being rendered instead.
   */
  async getCoverBlob(bookId: string): Promise<Blob | undefined> {
    try {
      const db = await getConnection();
      const manifest = await db.get('static_manifests', bookId);
      const raw = manifest?.coverBlob as unknown as Blob | ArrayBuffer | undefined;
      if (!raw) return undefined;
      return raw instanceof Blob ? raw : new Blob([raw]);
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Single manifest row (used by reprocessing to compute the updated row). */
  async getManifest(bookId: string): Promise<StaticManifestRow | undefined> {
    try {
      const db = await getConnection();
      return await db.get('static_manifests', bookId);
    } catch (error) {
      handleDbError(error);
    }
  }

  /** All manifest rows (backup generation, cover repair). */
  async listManifests(): Promise<StaticManifestRow[]> {
    try {
      const db = await getConnection();
      return await db.getAll('static_manifests');
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Retrieves the file content for a specific book. */
  async getBookFile(id: string): Promise<Blob | ArrayBuffer | undefined> {
    try {
      const db = await getConnection();
      const res = await db.get('static_resources', id);
      return res?.epubBlob;
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Retrieves all sections for a book (derived from spineItems, play order). */
  async getSections(bookId: string): Promise<SectionMetadata[]> {
    try {
      const db = await getConnection();
      const structure = await db.get('static_structure', bookId);
      if (!structure) return [];

      return structure.spineItems.map(item => ({
        id: `${bookId}-${item.id}`,
        bookId: bookId,
        sectionId: item.id,
        characterCount: item.characterCount,
        playOrder: item.index
      })).sort((a, b) => a.playOrder - b.playOrder);
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Retrieves the full static structure (TOC, Spine) for a book. */
  async getBookStructure(bookId: string): Promise<StaticStructureRow | undefined> {
    try {
      const db = await getConnection();
      return await db.get('static_structure', bookId);
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Returns a Map of bookId -> isOffloaded status for the requested book IDs.
   * (With no IDs there is nothing to answer against — the caller owns the
   * book list.)
   */
  async getOffloadedStatus(bookIds?: string[]): Promise<Map<string, boolean>> {
    try {
      const db = await getConnection();
      const result = new Map<string, boolean>();

      if (bookIds && bookIds.length > 0) {
        const tx = db.transaction('static_resources', 'readonly');
        const store = tx.objectStore('static_resources');

        // BOLT OPTIMIZATION: Avoid getAllKeys() across IDB boundary. Map to count() promises instead.
        // FURTHER OPTIMIZATION: Chunk parallel requests to avoid flooding the transaction/microtask queue.
        const CHUNK_SIZE = 100;
        for (let i = 0; i < bookIds.length; i += CHUNK_SIZE) {
          const chunk = bookIds.slice(i, i + CHUNK_SIZE);
          const promises = chunk.map(async (id) => {
            const count = await store.count(id);
            const exists = count > 0;
            logger.debug(`getOffloadedStatus: ${id} exists in static_resources? ${exists}`);
            result.set(id, !exists);
          });
          await Promise.all(promises);
        }
        await tx.done;
      }
      return result;
    } catch (error) {
      handleDbError(error);
    }
    return new Map();
  }

  /** Returns a Set of all book IDs that have binary content locally (NOT offloaded). */
  async getAvailableResourceIds(): Promise<Set<string>> {
    try {
      const db = await getConnection();
      const keys = await db.getAllKeys('static_resources');
      return new Set(keys as string[]);
    } catch (error) {
      handleDbError(error);
    }
    return new Set();
  }

  /** Locations cache read (epub.js render metrics). */
  async getLocations(bookId: string): Promise<BookLocations | undefined> {
    try {
      const db = await getConnection();
      const metrics = await db.get('cache_render_metrics', bookId);
      return metrics ? { bookId, locations: metrics.locations } : undefined;
    } catch (error) {
      handleDbError(error);
    }
  }

  /** All render-metrics rows (backup generation). */
  async listLocations(): Promise<CacheRenderMetricsRow[]> {
    try {
      const db = await getConnection();
      return await db.getAll('cache_render_metrics');
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Table LOCATIONS for a book — `{id, cfi, sectionId}` only, never a Blob.
   *
   * The consumers that only need CFIs (TTS grouping, the table-adaptation
   * work set) should use this rather than {@link getTableImages}, which
   * reads every table row of the book and wraps each stored ArrayBuffer in a
   * fresh Blob — 10–25 MB of Blob churn per section load on a table-heavy
   * book, twice per chapter boundary (load + prewarm). This walks the same
   * `by_bookId` index with a cursor and copies out three strings per row.
   *
   * A value cursor still deserializes the row (the image bytes cannot be
   * skipped without a schema change) but nothing is retained: no Blob wrapper
   * is ever constructed, and the engine memoizes the result per book.
   */
  async listTableLocations(bookId: string): Promise<TableLocation[]> {
    const locations: TableLocation[] = [];
    try {
      const db = await getConnection();
      const index = db
        .transaction('cache_table_images', 'readonly')
        .objectStore('cache_table_images')
        .index('by_bookId');
      let cursor = await index.openCursor(bookId);
      while (cursor) {
        const { id, cfi, sectionId } = cursor.value;
        locations.push({ id, cfi, sectionId });
        cursor = await cursor.continue();
      }
    } catch (error) {
      handleDbError(error);
    }
    return locations;
  }

  /**
   * Table snapshots for a book; stored ArrayBuffers re-wrapped as Blob for
   * the UI. `sectionId` narrows the rows INSIDE the repo so the callers that
   * only ever adapt one section do not materialize every other section's
   * image (filtering on the caller's side would happen only after every blob
   * had already been wrapped).
   */
  async getTableImages(bookId: string, sectionId?: string): Promise<TableImage[]> {
    try {
      const db = await getConnection();
      // Filtered reads stream a cursor and keep ONLY the matching rows: a
      // getAll would hold every table image of the book in one array at once
      // (only to discard all but one section's).
      const rows: TableImageRow[] = [];
      if (sectionId === undefined) {
        rows.push(...(await db.getAllFromIndex('cache_table_images', 'by_bookId', bookId)));
      } else {
        const index = db
          .transaction('cache_table_images', 'readonly')
          .objectStore('cache_table_images')
          .index('by_bookId');
        let cursor = await index.openCursor(bookId);
        while (cursor) {
          if (cursor.value.sectionId === sectionId) rows.push(cursor.value);
          cursor = await cursor.continue();
        }
      }
      return rows.map(row => {
        // imageBlob may be ArrayBuffer at runtime (stored as ArrayBuffer for WebKit IDB
        // compatibility). Re-wrapping MUST restore the MIME type: the capture site
        // (offscreen-renderer) always snaps tables as webp, and a bare new Blob([buf])
        // has type "" — which table adaptation forwards to Gemini as an inlineData
        // mimeType the API rejects.
        const rawImageBlob = row.imageBlob as unknown as Blob | ArrayBuffer;
        return {
          ...row,
          imageBlob: rawImageBlob instanceof ArrayBuffer
            ? new Blob([rawImageBlob], { type: 'image/webp' })
            : rawImageBlob,
        };
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /** TTS preparation row for a section (composite key). */
  async getTTSPreparation(bookId: string, sectionId: string): Promise<CacheTtsPreparationRow | undefined> {
    try {
      const db = await getConnection();
      const id = `${bookId}-${sectionId}`;
      return await db.get('cache_tts_preparation', id);
    } catch (error) {
      handleDbError(error);
    }
  }

  // ── Writers (all through the gate, synchronous populate) ───────────────

  /**
   * Ingests extracted book data (static + derived cache rows; user data is
   * Yjs's). Blob → ArrayBuffer normalization happens BEFORE the transaction
   * (WebKit's IDB structured clone does not support Blob), then all five
   * stores are populated in ONE gated transaction — an ingest is atomic.
   * Mode 'add' rejects (DatabaseError) when the book already exists.
   */
  async ingest(data: BookIngestData, mode: 'add' | 'overwrite' = 'add'): Promise<void> {
    try {
      // Pre-convert Blobs to ArrayBuffers before the transaction.
      const coverBlob = data.manifest.coverBlob;
      const manifestToStore: StaticManifestRow = {
        ...data.manifest,
        coverBlob: coverBlob instanceof Blob ? await coverBlob.arrayBuffer() : coverBlob,
      };

      const epubBlob = data.resource.epubBlob;
      const resourceToStore = {
        ...data.resource,
        epubBlob: epubBlob instanceof Blob ? await epubBlob.arrayBuffer() : epubBlob,
      };

      const tablesToStore: TableImageRow[] = await Promise.all(
        data.tableBatches.map(async (table) => ({
          ...table,
          imageBlob: await toArrayBuffer(table.imageBlob),
        }))
      );

      await write([
        'static_manifests', 'static_resources', 'static_structure',
        'cache_tts_preparation', 'cache_table_images'
      ], (tx) => {
        // populate is SYNCHRONOUS: requests are issued and their promises
        // deliberately silenced — a failed request (e.g. ConstraintError on
        // a duplicate 'add') aborts the transaction, and write() surfaces
        // the abort through tx.done. Awaiting individual requests here was
        // the intra-transaction-await WebKit hazard.
        const silence = (p: Promise<unknown>) => void p.catch(() => {});

        const manifestStore = tx.objectStore('static_manifests');
        const resourceStore = tx.objectStore('static_resources');
        const structureStore = tx.objectStore('static_structure');

        if (mode === 'overwrite') {
          silence(manifestStore.put(manifestToStore));
          silence(resourceStore.put(resourceToStore));
          silence(structureStore.put(data.structure));
        } else {
          silence(manifestStore.add(manifestToStore));
          silence(resourceStore.add(resourceToStore));
          silence(structureStore.add(data.structure));
        }

        // User data (overrides, progress, inventory) is handled by Yjs stores exclusively.

        const ttsStore = tx.objectStore('cache_tts_preparation');
        for (const batch of data.ttsContentBatches) {
          silence(mode === 'overwrite' ? ttsStore.put(batch) : ttsStore.add(batch));
        }

        const tableStore = tx.objectStore('cache_table_images');
        for (const table of tablesToStore) {
          silence(mode === 'overwrite' ? tableStore.put(table) : tableStore.add(table));
        }
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Atomically replaces a book's reprocessed derived rows: manifest update,
   * new structure, drop-and-rewrite of the per-section TTS preparation and
   * table-image cache rows. Old keys are read OUTSIDE the gate; the
   * replacement itself is one synchronous gated transaction.
   */
  async replaceDerivedContent(bookId: string, replacement: DerivedContentReplacement): Promise<void> {
    try {
      const tableRows: TableImageRow[] = await Promise.all(
        replacement.tableImages.map(async (table) => ({
          ...table,
          imageBlob: await toArrayBuffer(table.imageBlob),
        }))
      );

      const db = await getConnection();
      const oldPrepKeys = await db.getAllKeysFromIndex('cache_tts_preparation', 'by_bookId', bookId);
      const oldTableKeys = await db.getAllKeysFromIndex('cache_table_images', 'by_bookId', bookId);

      await write([
        'static_manifests', 'static_structure', 'cache_tts_preparation', 'cache_table_images'
      ], (tx) => {
        if (replacement.manifest) tx.objectStore('static_manifests').put(replacement.manifest);

        tx.objectStore('static_structure').put(replacement.structure);

        // TTS preparation cache: drop old section rows, write the new ones.
        const prepStore = tx.objectStore('cache_tts_preparation');
        for (const key of oldPrepKeys) prepStore.delete(key);
        for (const batch of replacement.ttsPrep) prepStore.put(batch);

        // Table-image cache: drop old rows, write the new ones (imageBlob now an ArrayBuffer).
        const tableStore = tx.objectStore('cache_table_images');
        for (const key of oldTableKeys) tableStore.delete(key);
        for (const table of tableRows) tableStore.put(table);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Updates the static structure (TOC) for a book. Used by Smart TOC enhancement. */
  async updateToc(bookId: string, toc: NavigationItem[]): Promise<void> {
    try {
      // Read outside the gate, then one synchronous put.
      const db = await getConnection();
      const structure = await db.get('static_structure', bookId);
      if (!structure) {
        throw new DatabaseError(`Book structure not found for ${bookId}`);
      }
      structure.toc = toc;
      await write(['static_structure'], (tx) => {
        tx.objectStore('static_structure').put(structure);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Deletes a book's static and cache rows in one gated transaction. Yjs
   * cleanup (content analysis, inventory) is orchestrated by
   * BookRepository.deleteBook. Index keys are read outside the gate.
   */
  async deleteBook(id: string): Promise<void> {
    try {
      const db = await getConnection();
      const prepKeys = await db.getAllKeysFromIndex('cache_tts_preparation', 'by_bookId', id);
      const tableKeys = await db.getAllKeysFromIndex('cache_table_images', 'by_bookId', id);

      await write([
        'static_manifests', 'static_resources', 'static_structure',
        'cache_render_metrics', 'cache_session_state', 'cache_tts_preparation',
        'cache_table_images', 'cache_search_text',
        'cache_embeddings', 'cache_embed_jobs'
      ], (tx) => {
        tx.objectStore('static_manifests').delete(id);
        tx.objectStore('static_resources').delete(id);
        tx.objectStore('static_structure').delete(id);
        tx.objectStore('cache_render_metrics').delete(id);
        tx.objectStore('cache_session_state').delete(id);
        // The persisted search corpus dies with the book.
        tx.objectStore('cache_search_text').delete(id);
        // The embedding vectors and their resumable job state die with
        // the book too — no vectors leak past deletion.
        tx.objectStore('cache_embeddings').delete(id);
        tx.objectStore('cache_embed_jobs').delete(id);

        const prepStore = tx.objectStore('cache_tts_preparation');
        for (const key of prepKeys) prepStore.delete(key);
        const tableStore = tx.objectStore('cache_table_images');
        for (const key of tableKeys) tableStore.delete(key);
      });

      logger.debug(`deleteBook: keys deleted for ${id}`);
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Offloads a book's file content: deletes the resource record entirely so
   * `getKey`/`count` report it as absent.
   */
  async offloadBook(id: string): Promise<void> {
    try {
      await write(['static_resources'], (tx) => {
        tx.objectStore('static_resources').delete(id);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Writes a restored book's binary content. Fingerprint verification (which
   * needs the ingestion pipeline) happens in ImportOrchestrator.restore
   * before this is called. Stores the file as ArrayBuffer (WebKit IDB does
   * not support Blob structured clone).
   */
  async restoreResource(id: string, epubArrayBuffer: ArrayBuffer): Promise<void> {
    try {
      // NOT a read-modify-write: `StaticResourceRow` is exactly
      // `{ bookId, epubBlob }` and this call replaces both, so reading the
      // existing row first only deserialized the ENTIRE old EPUB (tens of MB,
      // N times over during a full restore) to throw it away. The only
      // writers of this store are ingest() and this method, and both write
      // the whole row.
      await write(['static_resources'], (tx) => {
        tx.objectStore('static_resources').put({ bookId: id, epubBlob: epubArrayBuffer });
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Persist epub.js locations (last write wins). */
  async saveLocations(bookId: string, locations: string): Promise<void> {
    try {
      const db = await getConnection();
      const metrics = (await db.get('cache_render_metrics', bookId)) || { bookId, locations: '' };
      metrics.locations = locations;
      await write(['cache_render_metrics'], (tx) => {
        tx.objectStore('cache_render_metrics').put(metrics);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Persist one TTS preparation row. */
  async saveTTSPreparation(content: CacheTtsPreparationRow): Promise<void> {
    try {
      await write(['cache_tts_preparation'], (tx) => {
        tx.objectStore('cache_tts_preparation').put(content);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Bulk manifest restore/repair writer (backup restore, cover repair). */
  async putManifests(rows: StaticManifestRow[]): Promise<void> {
    if (rows.length === 0) return;
    try {
      await write(['static_manifests'], (tx) => {
        const store = tx.objectStore('static_manifests');
        for (const row of rows) store.put(row);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /** Bulk locations restore writer (backup restore). */
  async putLocations(rows: { bookId: string; locations: string }[]): Promise<void> {
    if (rows.length === 0) return;
    try {
      await write(['cache_render_metrics'], (tx) => {
        const store = tx.objectStore('cache_render_metrics');
        for (const row of rows) store.put(row);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  // ── Maintenance (orphan scan/prune over the book-content stores) ───────

  /**
   * Counts records whose parent book is not in `validBookIds`
   * (files, locations, TTS prep). Read-only.
   */
  async scanOrphans(validBookIds: Set<string>): Promise<{ files: number; locations: number; tts_prep: number }> {
    try {
      const db = await getConnection();

      const resourceKeys = await db.getAllKeys('static_resources');
      const orphanedFiles = resourceKeys.filter((k) => !validBookIds.has(k.toString()));

      const metricKeys = await db.getAllKeys('cache_render_metrics');
      const orphanedLocations = metricKeys.filter((k) => !validBookIds.has(k.toString()));

      let orphanedPrep = 0;
      const prepStore = db.transaction('cache_tts_preparation').objectStore('cache_tts_preparation');
      let prepCursor = await prepStore.openCursor();
      while (prepCursor) {
        if (!validBookIds.has(prepCursor.value.bookId)) {
          orphanedPrep++;
        }
        prepCursor = await prepCursor.continue();
      }

      return {
        files: orphanedFiles.length,
        locations: orphanedLocations.length,
        tts_prep: orphanedPrep
      };
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * The extraction version a previous re-ingest wave converged at — the
   * `app_metadata` marker written by {@link markReingestConverged}. While it
   * equals the current `TTS_EXTRACTION_VERSION` the wave can skip
   * {@link listTtsExtractionVersions} entirely, which otherwise materializes
   * EVERY sentence row of EVERY book on every boot just to read one integer
   * per book. Additive KV key — no DB version bump.
   */
  async getReingestConvergedVersion(): Promise<number | undefined> {
    try {
      const db = await getConnection();
      const stored = await db.get('app_metadata', APP_METADATA_KEYS.reingestScanConverged);
      return typeof stored === 'number' ? stored : undefined;
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Record that a wave finished with ZERO candidate books at `version` (a
   * converged library). Any later work that writes older rows is impossible
   * without also moving the constant, so the marker only goes stale by
   * design: a version bump makes it mismatch and the scan resumes.
   */
  async markReingestConverged(version: number): Promise<void> {
    try {
      await write(['app_metadata'], (tx) => {
        tx.objectStore('app_metadata').put(version, APP_METADATA_KEYS.reingestScanConverged);
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Per-book MINIMUM `extractionVersion` across all `cache_tts_preparation`
   * rows (missing stamp = implicit v1, reported as 1). The re-ingest wave's
   * candidacy scan: a single readonly cursor pass.
   */
  async listTtsExtractionVersions(): Promise<Map<string, number>> {
    const result = new Map<string, number>();
    try {
      const db = await getConnection();
      const prepStore = db.transaction('cache_tts_preparation').objectStore('cache_tts_preparation');
      let cursor = await prepStore.openCursor();
      while (cursor) {
        const row = cursor.value;
        const version = row.extractionVersion ?? 1;
        const current = result.get(row.bookId);
        if (current === undefined || version < current) {
          result.set(row.bookId, version);
        }
        cursor = await cursor.continue();
      }
    } catch (error) {
      handleDbError(error);
    }
    return result;
  }

  /** All TTS preparation rows for one book (re-ingest restamp/verification reads). */
  async listTtsPrepForBook(bookId: string): Promise<CacheTtsPreparationRow[]> {
    try {
      const db = await getConnection();
      return await db.getAllFromIndex('cache_tts_preparation', 'by_bookId', bookId);
    } catch (error) {
      handleDbError(error);
    }
    return [];
  }

  /**
   * Restamp a book's TTS preparation rows to `extractionVersion` IN PLACE —
   * the re-ingest fast path for rows proven byte-equivalent to a newer extraction
   * (no re-extraction). Rows are read OUTSIDE the gate; one synchronous
   * gated transaction writes the stamps.
   */
  async restampTtsPrep(bookId: string, extractionVersion: number): Promise<void> {
    try {
      const rows = await this.listTtsPrepForBook(bookId);
      if (rows.length === 0) return;
      await write(['cache_tts_preparation'], (tx) => {
        const store = tx.objectStore('cache_tts_preparation');
        for (const row of rows) {
          void store.put({ ...row, extractionVersion }).catch(() => {});
        }
      });
    } catch (error) {
      handleDbError(error);
    }
  }

  /**
   * Deletes all orphaned records found by {@link scanOrphans}. Orphan keys
   * are collected OUTSIDE the gate (readonly), then deleted in one gated
   * synchronous transaction.
   */
  async pruneOrphans(validBookIds: Set<string>): Promise<void> {
    try {
      const db = await getConnection();

      const fileKeys = (await db.getAllKeys('static_resources'))
        .filter((k) => !validBookIds.has(k.toString()));
      const locationKeys = (await db.getAllKeys('cache_render_metrics'))
        .filter((k) => !validBookIds.has(k.toString()));

      const prepKeys: string[] = [];
      {
        const prepStore = db.transaction('cache_tts_preparation').objectStore('cache_tts_preparation');
        let cursor = await prepStore.openCursor();
        while (cursor) {
          if (!validBookIds.has(cursor.value.bookId)) {
            prepKeys.push(cursor.primaryKey as string);
          }
          cursor = await cursor.continue();
        }
      }

      await write(['static_resources', 'cache_render_metrics', 'cache_tts_preparation'], (tx) => {
        const filesStore = tx.objectStore('static_resources');
        for (const key of fileKeys) filesStore.delete(key);
        const locationsStore = tx.objectStore('cache_render_metrics');
        for (const key of locationKeys) locationsStore.delete(key);
        const prepStore = tx.objectStore('cache_tts_preparation');
        for (const key of prepKeys) prepStore.delete(key);
      });
    } catch (error) {
      handleDbError(error);
    }
  }
}

/** Singleton (stateless; one instance keeps call sites uniform with the other repos). */
export const bookContent = new BookContentRepo();
